
import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../../../lib/supabase/client';
import { Check, CircleNotch, EnvelopeSimple, PencilSimple, Plus, Star, Trash, X } from '@phosphor-icons/react';
import { Chip, ImmoButton, LeerZustand, SchalterZeile, StatusPill } from '../../../components/immobilien/ui';
import { useMobilModus } from '../../../lib/immobilien/mobil';
import { FELD, Feld, Karte, LABEL, MEHRZEILIG } from './bereich/Formular';
import RichHtmlEditor from './RichHtmlEditor';

// ─── Types ───────────────────────────────────────────────────────────────────

interface EmailKontoEinstellungenProps {
  userProfile?: any;
  onOpenFormular: (konto: any) => void;
}

interface EmailKonto {
  id: string;
  bezeichnung: string;
  email_adresse: string;
  ist_standard: boolean;
  aktiv: boolean;
  letzter_sync: string | null;
  sync_status: string | null;
  created_at: string;
}

interface Signatur {
  id: string;
  name: string;
  html_signatur: string;
  ist_standard: boolean;
  created_at: string;
}

interface Briefpapier {
  id: string;
  name: string;
  html: string;
  ist_standard: boolean;
  created_at: string;
}

// ─── Component ───────────────────────────────────────────────────────────────

const EmailKontoEinstellungen: React.FC<EmailKontoEinstellungenProps> = ({ userProfile, onOpenFormular }) => {
  const { mobil } = useMobilModus();
  // Konten
  const [konten, setKonten] = useState<EmailKonto[]>([]);
  const [kontenLoading, setKontenLoading] = useState(true);

  // Signaturen
  const [signaturen, setSignaturen] = useState<Signatur[]>([]);
  const [signaturenLoading, setSignaturenLoading] = useState(true);
  const [editSignatur, setEditSignatur] = useState<Signatur | null>(null);
  const [neuSignaturName, setNeuSignaturName] = useState('');
  const [neuSignaturHtml, setNeuSignaturHtml] = useState('');
  const [neuSignaturStandard, setNeuSignaturStandard] = useState(false);
  const [showSignaturForm, setShowSignaturForm] = useState(false);
  const [signaturSaving, setSignaturSaving] = useState(false);

  // Briefpapier (firmenweit)
  const [briefpapier, setBriefpapier] = useState<Briefpapier[]>([]);
  const [briefpapierLoading, setBriefpapierLoading] = useState(true);
  const [editBriefpapier, setEditBriefpapier] = useState<Briefpapier | null>(null);
  const [bpName, setBpName] = useState('');
  const [bpHtml, setBpHtml] = useState('');
  const [bpStandard, setBpStandard] = useState(false);
  const [showBriefpapierForm, setShowBriefpapierForm] = useState(false);
  const [bpSaving, setBpSaving] = useState(false);
  const bpPreviewRef = useRef<HTMLIFrameElement>(null);

  // ── Fetch ──────────────────────────────────────────────────────────────────

  useEffect(() => {
    fetchKonten();
    fetchSignaturen();
    fetchBriefpapier();
  }, []);

  const fetchBriefpapier = async () => {
    setBriefpapierLoading(true);
    try {
      const { data, error } = await supabase
        .from('email_briefpapier')
        .select('*')
        .order('ist_standard', { ascending: false })
        .order('created_at', { ascending: false });
      if (error) throw error;
      setBriefpapier(data || []);
    } catch (err) {
      console.error('Fehler beim Laden der Briefpapiere:', err);
    } finally {
      setBriefpapierLoading(false);
    }
  };

  const fetchKonten = async () => {
    setKontenLoading(true);
    try {
      const { data, error } = await supabase
        .from('email_konten')
        .select('*')
        .order('ist_standard', { ascending: false });
      if (error) throw error;
      setKonten(data || []);
    } catch (err) {
      console.error('Fehler beim Laden der Konten:', err);
    } finally {
      setKontenLoading(false);
    }
  };

  const fetchSignaturen = async () => {
    setSignaturenLoading(true);
    try {
      const { data, error } = await supabase
        .from('email_signaturen')
        .select('*')
        .order('ist_standard', { ascending: false })
        .order('created_at', { ascending: false });
      if (error) throw error;
      setSignaturen(data || []);
    } catch (err) {
      console.error('Fehler beim Laden der Signaturen:', err);
    } finally {
      setSignaturenLoading(false);
    }
  };

  // ── Signatur Aktionen ──────────────────────────────────────────────────────

  const openSignaturForm = (sig?: Signatur) => {
    if (sig) {
      setEditSignatur(sig);
      setNeuSignaturName(sig.name);
      setNeuSignaturHtml(sig.html_signatur);
      setNeuSignaturStandard(sig.ist_standard);
    } else {
      setEditSignatur(null);
      setNeuSignaturName('');
      setNeuSignaturHtml('');
      setNeuSignaturStandard(false);
    }
    setShowSignaturForm(true);
  };

  const closeSignaturForm = () => {
    setShowSignaturForm(false);
    setEditSignatur(null);
    setNeuSignaturName('');
    setNeuSignaturHtml('');
    setNeuSignaturStandard(false);
  };

  const handleSaveSignatur = async () => {
    if (!neuSignaturName.trim() || !neuSignaturHtml.trim()) return;
    setSignaturSaving(true);
    try {
      // RLS: email_signaturen ist pro Nutzer (user_id = auth.uid()) — beim Insert MUSS user_id gesetzt sein.
      const { data: { user } } = await supabase.auth.getUser();
      const payload = { name: neuSignaturName.trim(), html_signatur: neuSignaturHtml.trim(), ist_standard: neuSignaturStandard, user_id: user?.id };
      let savedId = editSignatur?.id;
      if (editSignatur) {
        const { error } = await supabase.from('email_signaturen').update(payload).eq('id', editSignatur.id);
        if (error) throw error;
      } else {
        const { data, error } = await supabase.from('email_signaturen').insert(payload).select('id').single();
        if (error) throw error;
        savedId = data?.id;
      }
      // Es darf nur EINE Standard-Signatur geben: bei true die anderen zurücksetzen.
      if (neuSignaturStandard && savedId) {
        await supabase.from('email_signaturen').update({ ist_standard: false }).neq('id', savedId);
      }
      await fetchSignaturen();
      closeSignaturForm();
    } catch (err) {
      console.error('Fehler beim Speichern der Signatur:', err);
    } finally {
      setSignaturSaving(false);
    }
  };

  // Schnellaktion aus der Liste: diese Signatur zur Standard-Signatur machen.
  const setAlsStandard = async (id: string) => {
    try {
      await supabase.from('email_signaturen').update({ ist_standard: true }).eq('id', id);
      await supabase.from('email_signaturen').update({ ist_standard: false }).neq('id', id);
      fetchSignaturen();
    } catch (err) {
      console.error('Fehler beim Setzen der Standard-Signatur:', err);
    }
  };

  const handleDeleteSignatur = async (id: string) => {
    try {
      const { error } = await supabase.from('email_signaturen').delete().eq('id', id);
      if (error) throw error;
      setSignaturen(prev => prev.filter(s => s.id !== id));
    } catch (err) {
      console.error('Fehler beim Loeschen der Signatur:', err);
    }
  };

  // ── Briefpapier Aktionen ────────────────────────────────────────────────────

  const openBriefpapierForm = (bp?: Briefpapier) => {
    if (bp) {
      setEditBriefpapier(bp);
      setBpName(bp.name);
      setBpHtml(bp.html);
      setBpStandard(bp.ist_standard);
    } else {
      setEditBriefpapier(null);
      setBpName('');
      setBpHtml('');
      setBpStandard(false);
    }
    setShowBriefpapierForm(true);
  };

  const closeBriefpapierForm = () => {
    setShowBriefpapierForm(false);
    setEditBriefpapier(null);
    setBpName('');
    setBpHtml('');
    setBpStandard(false);
  };

  const handleSaveBriefpapier = async () => {
    if (!bpName.trim() || !bpHtml.trim()) return;
    setBpSaving(true);
    try {
      const payload = { name: bpName.trim(), html: bpHtml, ist_standard: bpStandard };
      let savedId = editBriefpapier?.id;
      if (editBriefpapier) {
        const { error } = await supabase.from('email_briefpapier').update(payload).eq('id', editBriefpapier.id);
        if (error) throw error;
      } else {
        const { data, error } = await supabase.from('email_briefpapier').insert(payload).select('id').single();
        if (error) throw error;
        savedId = data?.id;
      }
      if (bpStandard && savedId) {
        await supabase.from('email_briefpapier').update({ ist_standard: false }).neq('id', savedId);
      }
      await fetchBriefpapier();
      closeBriefpapierForm();
    } catch (err) {
      console.error('Fehler beim Speichern des Briefpapiers:', err);
    } finally {
      setBpSaving(false);
    }
  };

  const setBriefpapierStandard = async (id: string) => {
    try {
      await supabase.from('email_briefpapier').update({ ist_standard: true }).eq('id', id);
      await supabase.from('email_briefpapier').update({ ist_standard: false }).neq('id', id);
      fetchBriefpapier();
    } catch (err) {
      console.error('Fehler beim Setzen des Standard-Briefpapiers:', err);
    }
  };

  const handleDeleteBriefpapier = async (id: string) => {
    try {
      const { error } = await supabase.from('email_briefpapier').delete().eq('id', id);
      if (error) throw error;
      setBriefpapier(prev => prev.filter(b => b.id !== id));
    } catch (err) {
      console.error('Fehler beim Löschen des Briefpapiers:', err);
    }
  };

  // Live-Vorschau des Briefpapier-Formulars ({{inhalt}}/{{signatur}} durch Beispiel ersetzt).
  useEffect(() => {
    if (showBriefpapierForm && bpPreviewRef.current) {
      const doc = bpPreviewRef.current.contentDocument;
      if (doc) {
        doc.open();
        doc.write(bpHtml.split('{{inhalt}}').join('<p><em>[Hier steht der geschriebene Text]</em></p>').split('{{signatur}}').join('<p><em>[Signatur]</em></p>'));
        doc.close();
      }
    }
  }, [showBriefpapierForm, bpHtml]);

  // ── Helpers ────────────────────────────────────────────────────────────────

  const getSyncBadge = (status: string | null): { label: string; ton: 'ok' | 'bad' | 'neutral' } => {
    switch (status) {
      case 'ok': return { label: 'Verbunden', ton: 'ok' };
      case 'fehler': return { label: 'Fehler', ton: 'bad' };
      default: return { label: 'Ausstehend', ton: 'neutral' };
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Nie';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return 'Nie';
    return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const h = mobil ? 44 : 38;
  const iconKnopf = (farbe = 'var(--ink2)'): React.CSSProperties => ({
    width: h, height: h, borderRadius: 12, border: 'none', background: 'transparent', color: farbe, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0,
  });
  const laedt = <span className="flex justify-center" style={{ padding: 20 }}><CircleNotch size={22} className="animate-spin" color="var(--ink3)" /></span>;
  // Vorschauzeile: HTML als reinen Text lesen (wandelt auch &nbsp; & Co. um).
  const vorschauText = (html: string) =>
    (new DOMParser().parseFromString(html.replace(/<[^>]*>/g, ' '), 'text/html').body.textContent || '').replace(/\s+/g, ' ').trim().slice(0, 90) || '—';
  const zeile = (key: string, name: string, standard: boolean, html: string, aktionen: React.ReactNode) => (
    <div key={key} className="flex items-center min-w-0" style={{ gap: 10, borderRadius: 18, padding: '12px 10px 12px 14px', background: 'var(--fill)', boxShadow: 'inset 0 0 0 1px var(--line)' }}>
      <div className="flex flex-col flex-1 min-w-0" style={{ gap: 2 }}>
        <span className="flex items-center min-w-0" style={{ gap: 6 }}>
          <span className="truncate" style={{ fontSize: 14, fontWeight: 800 }}>{name}</span>
          {standard && <Chip ton="acc" groesse="xs" icon={Star}>Standard</Chip>}
        </span>
        <span className="truncate" style={{ fontSize: 12, color: 'var(--ink3)' }}>{vorschauText(html)}</span>
      </div>
      <div className="flex items-center shrink-0">{aktionen}</div>
    </div>
  );
  const formFuss = (onAbbrechen: () => void, onSpeichern: () => void, aus: boolean, laeuftJetzt: boolean) => (
    <div className="flex justify-end" style={{ gap: 8 }}>
      <ImmoButton groesse={44} icon={X} onClick={onAbbrechen}>Abbrechen</ImmoButton>
      <ImmoButton art="akzent" groesse={44} icon={laeuftJetzt ? CircleNotch : Check} onClick={onSpeichern} disabled={aus}>Speichern</ImmoButton>
    </div>
  );

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col min-w-0" style={{ gap: 14, maxWidth: 980 }}>
      {/* ── Angebundenes Postfach ─────────────────────────────────────────── */}
      <Karte kleinTitel titel="Angebundenes Postfach">
        {kontenLoading ? laedt : konten.length === 0 ? (
          <LeerZustand icon={EnvelopeSimple} titel="Kein Postfach angebunden" text="Das Postfach wird zentral über Microsoft 365 (Graph) angebunden und per Cron synchronisiert." />
        ) : konten.map(konto => {
          const badge = getSyncBadge(konto.sync_status);
          return (
            <div key={konto.id} className="flex items-center flex-wrap" style={{ gap: 12 }}>
              <span className="flex items-center justify-center shrink-0" style={{ width: 46, height: 46, borderRadius: 15, background: 'var(--acc-soft)' }}>
                <EnvelopeSimple size={22} weight="fill" color="var(--acc)" />
              </span>
              <div className="flex flex-col min-w-0" style={{ flex: '1 1 220px', gap: 2 }}>
                <span className="flex items-center flex-wrap" style={{ gap: 6, fontSize: 16, fontWeight: 800 }}>
                  <span className="truncate">{konto.bezeichnung}</span>
                  {konto.ist_standard && <Chip ton="acc" groesse="xs">Standard</Chip>}
                </span>
                <span style={{ fontSize: 13, color: 'var(--ink2)', overflowWrap: 'anywhere' }}>{konto.email_adresse} · Microsoft 365 (Graph)</span>
                <span style={{ fontSize: 12, color: 'var(--ink3)' }}>Letzter Sync: {formatDate(konto.letzter_sync)}</span>
              </div>
              <StatusPill status={badge.ton === 'ok' ? 'aktiv' : badge.ton === 'bad' ? 'gesperrt' : 'inaktiv'} label={badge.label} />
              <button type="button" onClick={() => onOpenFormular(konto)} title="Bearbeiten" aria-label="Bearbeiten" className="immo-hover-flaeche" style={iconKnopf()}>
                <PencilSimple size={18} />
              </button>
            </div>
          );
        })}
      </Karte>

      {/* ── Signaturen ──────────────────────────────────────────────────────── */}
      <Karte titel="Signaturen" aktion={<ImmoButton art="akzent" groesse={mobil ? 44 : 36} icon={Plus} onClick={() => openSignaturForm()}>Neue Signatur</ImmoButton>}>
        {showSignaturForm && (
          <div className="flex flex-col" style={{ gap: 10, borderRadius: 18, padding: 14, background: 'var(--fill)' }}>
            <span style={{ fontSize: 14, fontWeight: 800 }}>{editSignatur ? 'Signatur bearbeiten' : 'Neue Signatur'}</span>
            <Feld label="Name *">
              <input type="text" value={neuSignaturName} onChange={e => setNeuSignaturName(e.target.value)} placeholder='z.B. "Standard-Signatur"' style={FELD} />
            </Feld>
            <div className="flex flex-col" style={{ gap: 6 }}>
              <span style={LABEL}>Signatur *</span>
              <RichHtmlEditor
                key={editSignatur?.id ?? 'neu'}
                value={neuSignaturHtml}
                onChange={setNeuSignaturHtml}
                placeholder="Ihre Signatur — z. B. Name, Firma, Telefon, Web…"
                minHeight={160}
                mobil={mobil}
              />
            </div>
            <SchalterZeile an={neuSignaturStandard} onChange={setNeuSignaturStandard} label="Als Standard-Signatur (wird bei neuen Mails automatisch eingefügt)" />
            {formFuss(closeSignaturForm, handleSaveSignatur, !neuSignaturName.trim() || !neuSignaturHtml.trim() || signaturSaving, signaturSaving)}
          </div>
        )}
        {signaturenLoading ? laedt : signaturen.length === 0 && !showSignaturForm ? (
          <span style={{ fontSize: 13, color: 'var(--ink3)' }}>Noch keine Signaturen erstellt</span>
        ) : signaturen.map(sig => zeile(sig.id, sig.name, sig.ist_standard, sig.html_signatur, (
          <>
            {!sig.ist_standard && (
              <button type="button" onClick={() => setAlsStandard(sig.id)} title="Als Standard-Signatur setzen" aria-label="Als Standard-Signatur setzen" className="immo-hover-flaeche" style={iconKnopf()}><Star size={18} /></button>
            )}
            <button type="button" onClick={() => openSignaturForm(sig)} title="Bearbeiten" aria-label="Bearbeiten" className="immo-hover-flaeche" style={iconKnopf()}><PencilSimple size={18} /></button>
            <button type="button" onClick={() => handleDeleteSignatur(sig.id)} title="Löschen" aria-label="Löschen" className="immo-hover-flaeche" style={iconKnopf('var(--bad)')}><Trash size={18} /></button>
          </>
        )))}
      </Karte>

      {/* ── Briefpapier ─────────────────────────────────────────────────────── */}
      <Karte
        titel="Briefpapier"
        untertitel={<>Firmenweiter HTML-Rahmen für Mails. Platzhalter <code style={CODE}>{'{{inhalt}}'}</code> = geschriebener Text, <code style={CODE}>{'{{signatur}}'}</code> = Standard-Signatur (optional).</>}
        aktion={<ImmoButton art="akzent" groesse={mobil ? 44 : 36} icon={Plus} onClick={() => openBriefpapierForm()}>Neues Briefpapier</ImmoButton>}
      >
        {showBriefpapierForm && (
          <div className="flex flex-col" style={{ gap: 10, borderRadius: 18, padding: 14, background: 'var(--fill)' }}>
            <span style={{ fontSize: 14, fontWeight: 800 }}>{editBriefpapier ? 'Briefpapier bearbeiten' : 'Neues Briefpapier'}</span>
            <Feld label="Name *">
              <input type="text" value={bpName} onChange={e => setBpName(e.target.value)} placeholder='z.B. "Standard-Briefpapier"' style={FELD} />
            </Feld>
            <div className="flex flex-wrap" style={{ gap: 12 }}>
              <div className="flex flex-col min-w-0" style={{ flex: '1 1 300px', gap: 6 }}>
                <Feld label="HTML *" hinweis={<>HTML einfügen; <code style={CODE}>{'{{inhalt}}'}</code> dorthin, wo dein Text erscheinen soll (optional <code style={CODE}>{'{{signatur}}'}</code>).</>}>
                  <textarea value={bpHtml} onChange={e => setBpHtml(e.target.value)} rows={14} placeholder="<html>…{{inhalt}}…{{signatur}}…</html>"
                    style={{ ...MEHRZEILIG, fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 12 }} />
                </Feld>
              </div>
              <div className="flex flex-col min-w-0" style={{ flex: '1 1 260px', gap: 6 }}>
                <span style={LABEL}>Vorschau</span>
                <div className="overflow-hidden" style={{ height: 320, borderRadius: 14, background: '#fff', boxShadow: 'inset 0 0 0 1px var(--line)' }}>
                  {bpHtml ? (
                    <iframe ref={bpPreviewRef} sandbox="allow-same-origin" title="Briefpapier-Vorschau" className="w-full h-full border-0" />
                  ) : (
                    <div className="flex items-center justify-center h-full" style={{ fontSize: 12, color: '#9ca3af' }}>HTML eingeben für Vorschau</div>
                  )}
                </div>
              </div>
            </div>
            <SchalterZeile an={bpStandard} onChange={setBpStandard} label="Als Standard-Briefpapier (bei neuen Mails vorausgewählt)" />
            {formFuss(closeBriefpapierForm, handleSaveBriefpapier, !bpName.trim() || !bpHtml.trim() || bpSaving, bpSaving)}
          </div>
        )}
        {briefpapierLoading ? laedt : briefpapier.length === 0 && !showBriefpapierForm ? (
          <span style={{ fontSize: 13, color: 'var(--ink3)' }}>Noch kein Briefpapier erstellt</span>
        ) : briefpapier.map(bp => zeile(bp.id, bp.name, bp.ist_standard, bp.html, (
          <>
            {!bp.ist_standard && (
              <button type="button" onClick={() => setBriefpapierStandard(bp.id)} title="Als Standard setzen" aria-label="Als Standard setzen" className="immo-hover-flaeche" style={iconKnopf()}><Star size={18} /></button>
            )}
            <button type="button" onClick={() => openBriefpapierForm(bp)} title="Bearbeiten" aria-label="Bearbeiten" className="immo-hover-flaeche" style={iconKnopf()}><PencilSimple size={18} /></button>
            <button type="button" onClick={() => handleDeleteBriefpapier(bp.id)} title="Löschen" aria-label="Löschen" className="immo-hover-flaeche" style={iconKnopf('var(--bad)')}><Trash size={18} /></button>
          </>
        )))}
      </Karte>
    </div>
  );
};

const CODE: React.CSSProperties = { padding: '1px 5px', borderRadius: 6, background: 'var(--fill2)', fontSize: 11, color: 'var(--ink2)' };

export default EmailKontoEinstellungen;
